import { useEffect, useState } from "react";
import api from "@/lib/api";
import KeyData from "@/components/KeyData";
import CelestialLoader from "@/components/CelestialLoader";
import { Check, Circle } from "lucide-react";
import { toast } from "sonner";

export default function Mission() {
  const [m, setM] = useState(null);
  const [completing, setCompleting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/mission/current");
      setM(r.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  if (!m) return <CelestialLoader />;

  const currentIdx = m.sub_tasks.findIndex(st => !st.completed);
  const completedCount = m.sub_tasks.filter(st => st.completed).length;

  const completeCurrent = async () => {
    if (currentIdx < 0) return;
    setCompleting(true);
    try {
      await api.post("/mission/complete-subtask", { subtask_id: m.sub_tasks[currentIdx].id });
      toast.success("One step closer.");
      await load();
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="pb-32 animate-gentle-rise">
      <p className="text-caption uppercase tracking-widest" style={{ color: "var(--vedari-gold)" }}>Your Mission</p>
      <h1 className="font-display text-[40px] leading-tight mt-2" data-testid="mission-name">{m.name}</h1>
      <p className="text-body-lg mt-3 leading-relaxed font-serif italic" style={{ color: "var(--vedari-ink-soft)" }}>
        {m.completion_definition}
      </p>

      <div className="flex items-center gap-6 mt-6 mb-2 pb-6" style={{ borderBottom: "1px solid var(--vedari-divider)" }}>
        <div>
          <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>Progress</p>
          <p className="mt-1"><KeyData>{completedCount}</KeyData> / <KeyData>{m.sub_tasks.length}</KeyData> milestones</p>
        </div>
        <div>
          <p className="text-caption uppercase tracking-wider" style={{ color: "var(--vedari-ink-soft)" }}>Duration</p>
          <p className="mt-1"><KeyData>{m.duration_days}</KeyData> days</p>
        </div>
      </div>

      {/* Visual milestone path */}
      <div className="my-8" data-testid="mission-path">
        <svg viewBox="0 0 320 200" className="w-full h-32">
          <path
            d={pathD(m.sub_tasks.length)}
            fill="none"
            stroke="var(--vedari-divider)"
            strokeWidth="2"
            strokeDasharray="3 5"
          />
          {m.sub_tasks.map((st, i) => {
            const pos = nodePos(i, m.sub_tasks.length);
            const isCurrent = i === currentIdx;
            const isDone = st.completed;
            return (
              <g key={st.id}>
                <circle
                  cx={pos.x} cy={pos.y}
                  r={isCurrent ? 11 : 7}
                  fill={isDone ? "var(--vedari-gold)" : isCurrent ? "var(--vedari-surface)" : "var(--vedari-surface)"}
                  stroke="var(--vedari-gold)"
                  strokeWidth={isCurrent ? 2 : 1.5}
                  opacity={isDone || isCurrent ? 1 : 0.45}
                  data-testid={`milestone-${i}`}
                />
                {isCurrent && (
                  <circle cx={pos.x} cy={pos.y} r={16} fill="none" stroke="var(--vedari-gold)" strokeWidth="1" opacity="0.35">
                    <animate attributeName="r" from="13" to="20" dur="3s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.4" to="0" dur="3s" repeatCount="indefinite" />
                  </circle>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Subtasks list */}
      <div className="space-y-3" data-testid="mission-subtasks">
        {m.sub_tasks.map((st, i) => {
          const isCurrent = i === currentIdx;
          const isDone = st.completed;
          return (
            <div key={st.id}
              className="flex items-start gap-3 p-4 rounded-xl transition-all"
              style={{
                backgroundColor: isCurrent ? "var(--vedari-surface)" : "transparent",
                border: `1px solid ${isCurrent ? "var(--vedari-gold)" : "var(--vedari-divider)"}`,
                opacity: !isDone && !isCurrent && i > currentIdx && currentIdx >= 0 ? 0.5 : 1,
              }}
              data-testid={`subtask-row-${i}`}
            >
              <div className="mt-0.5">
                {isDone ? (
                  <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--vedari-gold)" }}>
                    <Check size={14} strokeWidth={2} color="white" />
                  </div>
                ) : (
                  <Circle size={22} strokeWidth={1.5} style={{ color: isCurrent ? "var(--vedari-gold)" : "var(--vedari-ink-soft)" }} />
                )}
              </div>
              <div className="flex-1">
                <p className="text-caption" style={{ color: "var(--vedari-ink-soft)" }}>Step <KeyData>{String(i+1).padStart(2, "0")}</KeyData></p>
                <p className="text-body mt-1 leading-relaxed" style={{ textDecoration: isDone ? "line-through" : "none", color: "var(--vedari-ink)" }}>
                  {st.title}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky complete button */}
      {currentIdx >= 0 && (
        <div className="fixed bottom-20 left-0 right-0 px-6 pb-4 pt-4 z-20 max-w-md mx-auto"
          style={{ background: "linear-gradient(to top, var(--vedari-cream), transparent)" }}>
          <button onClick={completeCurrent} disabled={completing} className="vedari-btn-primary w-full" data-testid="mission-complete-subtask-btn">
            {completing ? "Saving…" : "Mark sub-task complete"}
          </button>
        </div>
      )}
      {currentIdx < 0 && (
        <div className="mt-8 text-center vedari-card">
          <p className="font-serif text-h2">Mission complete</p>
          <p className="text-body-sm mt-2 font-serif italic" style={{ color: "var(--vedari-ink-soft)" }}>You walked the full path. Rest before the next chapter.</p>
        </div>
      )}
    </div>
  );
}

function pathD(n) {
  // Wavy hand-drawn curve across width
  const points = [];
  for (let i = 0; i < n; i++) {
    const pos = nodePos(i, n);
    points.push(pos);
  }
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    const prev = points[i-1];
    const cx = (prev.x + p.x) / 2;
    const cy = (prev.y + p.y) / 2 + (i % 2 === 0 ? -10 : 10);
    d += ` Q ${cx} ${cy}, ${p.x} ${p.y}`;
  }
  return d;
}

function nodePos(i, n) {
  const padding = 20;
  const w = 320 - padding * 2;
  const x = padding + (n > 1 ? (i / (n - 1)) * w : w / 2);
  const y = 100 + Math.sin(i * 0.9) * 32;
  return { x, y };
}
